import { parseFormula } from './formulaUtils';

// Roman numerals for names like "Iron(III) oxide".
const ROMAN_NUMERALS = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII'];

export const ANION_NAMES = Object.freeze({
  O: 'oxide',
  F: 'fluoride',
  Cl: 'chloride',
  Br: 'bromide',
  I: 'iodide',
  S: 'sulfide',
  Se: 'selenide',
  Te: 'telluride',
  Po: 'polonide',
  N: 'nitride',
  H: 'hydride',
  C: 'carbide',
  B: 'boride',
  Si: 'silicide',
  P: 'phosphide',
  As: 'arsenide',
});

// Charges used to work out Roman numerals like Iron(III). Nitrides, hydrides, carbides,
// borides, silicides, phosphides and arsenides often don't follow simple charges, so they get none.
const ANION_CHARGES = Object.freeze({ O: 2, S: 2, Se: 2, Te: 2, Po: 2, F: 1, Cl: 1, Br: 1, I: 1 });

// Names that don't follow the simple pattern, or have a well-known common name.
const SPECIAL_NAMES = Object.freeze({
  Na2O2: 'Sodium peroxide',
  KO2: 'Potassium superoxide',
  RbO2: 'Rubidium superoxide',
  CsO2: 'Caesium superoxide',
  Fe3O4: 'Iron(II,III) oxide (magnetite)',
  Mn3O4: 'Manganese(II,III) oxide',
  Co3O4: 'Cobalt(II,III) oxide',
  Pr6O11: 'Praseodymium(III,IV) oxide',
  Tb4O7: 'Terbium(III,IV) oxide',
  Fe3C: 'Iron carbide (cementite)',
  PdH: 'Palladium hydride',
  NaCl: 'Sodium chloride (table salt)',
  Fe2O3: 'Iron(III) oxide (rust)',
  CaO: 'Calcium oxide (quicklime)',
  TiO2: 'Titanium dioxide',
  SnO2: 'Tin(IV) oxide',
  ZnS: 'Zinc sulfide',
  PbS: 'Lead(II) sulfide (galena)',
  HgS: 'Mercury(II) sulfide (cinnabar)',
  MoS2: 'Molybdenum disulfide',
  Sb2S3: 'Antimony trisulfide (stibnite)',
  WC: 'Tungsten carbide',
  UO2: 'Uranium dioxide',
  OsO4: 'Osmium tetroxide',
  AuTe2: 'Gold telluride (calaverite)',
  GaAs: 'Gallium arsenide',
  NiAs: 'Nickel arsenide (nickeline)',
  CdTe: 'Cadmium telluride',
  CaC2: 'Calcium carbide',
  MgB2: 'Magnesium boride',
  LaB6: 'Lanthanum hexaboride',
  CaSi2: 'Calcium disilicide',
  PtAs2: 'Platinum arsenide (sperrylite)',
});

/**
 * "Fe", "Fe2O3" -> "Iron(III) oxide (rust)".
 */
export function nameBinaryCompound(metal, formula, anionSymbol, { useRomanNumeral = false } = {}) {
  if (SPECIAL_NAMES[formula]) return SPECIAL_NAMES[formula];
  const counts = parseFormula(formula);
  let numeral = '';
  const anionCharge = ANION_CHARGES[anionSymbol];
  if (useRomanNumeral && anionCharge) {
    const charge = (counts[anionSymbol] * anionCharge) / counts[metal.symbol];
    if (Number.isInteger(charge) && ROMAN_NUMERALS[charge]) numeral = `(${ROMAN_NUMERALS[charge]})`;
  }
  return `${metal.name}${numeral} ${ANION_NAMES[anionSymbol]}`;
}

/**
 * Hydroxide formula for a metal with the given charge: NaOH, Ca(OH)2, La(OH)3.
 */
export function buildHydroxideFormula(symbol, charge) {
  return charge === 1 ? `${symbol}OH` : `${symbol}(OH)${charge}`;
}

export function nameHydroxide(metal, charge, { useRomanNumeral = false } = {}) {
  const numeral = useRomanNumeral && ROMAN_NUMERALS[charge] ? `(${ROMAN_NUMERALS[charge]})` : '';
  return `${metal.name}${numeral} hydroxide`;
}
